import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  RefreshCw,
  User,
  CheckCircle,
  Clock,
  Bell,
  Search,
  Lock,
  Eye,
  EyeOff,
  AlertTriangle,
  Send,
  MapPin,
  Calendar,
  CreditCard,
  Wifi,
  WifiOff,
  FileText,
  Shield,
  X,
  Sparkles,
  Phone,
  RotateCcw,
  Paperclip
} from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import WorkerDocuments from '@/components/WorkerDocuments';

import {
  OpsBackground,
  OpsStatusBar,
  KpiTile,
  OpsSearchBar
} from '@/components/passport-tracker/OpsComponents';

// Helper function to format base64 image with proper data URI prefix
const formatBase64Image = (base64String) => {
  if (!base64String) return null;
  if (base64String.startsWith('data:image')) return base64String;
  return `data:image/jpeg;base64,${base64String}`;
};

// ========== ADVANCED FUZZY SEARCH SYSTEM ==========

// Normalize text for search
const normalizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s\u0600-\u06FF]/g, '')
    .trim();
};

// Calculate Levenshtein distance
const levenshteinDistance = (str1, str2) => {
  const m = str1.length;
  const n = str2.length;
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) dp[i][j] = dp[i - 1][j - 1];
      else dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
};

// Calculate similarity score
const calculateSimilarity = (str1, str2) => {
  if (!str1 || !str2) return 0;
  const norm1 = normalizeText(str1);
  const norm2 = normalizeText(str2);
  if (!norm1 || !norm2) return 0;
  const maxLen = Math.max(norm1.length, norm2.length);
  if (maxLen === 0) return 100;
  const distance = levenshteinDistance(norm1, norm2);
  return Math.round((1 - distance / maxLen) * 100);
};

// Fuzzy match function
const fuzzyMatch = (text, query, threshold = 60) => {
  if (!text || !query) return { match: false, score: 0 };
  const normText = normalizeText(text);
  const normQuery = normalizeText(query);
  if (!normText || !normQuery) return { match: false, score: 0 };
  
  if (normText.includes(normQuery)) return { match: true, score: 100 };
  
  const textWords = normText.split(/\s+/);
  const queryWords = normQuery.split(/\s+/);
  let totalScore = 0, matchedWords = 0;
  
  for (const qWord of queryWords) {
    let bestWordScore = 0;
    for (const tWord of textWords) {
      if (tWord.startsWith(qWord)) bestWordScore = Math.max(bestWordScore, 95);
      else if (tWord.includes(qWord)) bestWordScore = Math.max(bestWordScore, 85);
      else {
        const similarity = calculateSimilarity(qWord, tWord);
        if (similarity >= threshold) bestWordScore = Math.max(bestWordScore, similarity);
      }
    }
    if (bestWordScore > 0) { totalScore += bestWordScore; matchedWords++; }
  }
  
  const finalScore = queryWords.length > 0 ? Math.round(totalScore / queryWords.length) : 0;
  return { match: matchedWords > 0 && finalScore >= threshold, score: finalScore };
};

// Advanced search function
const advancedSearch = (items, query, searchFields) => {
  if (!query || !query.trim()) return items.map(item => ({ ...item, searchScore: 0 }));
  
  const results = items.map(item => {
    let maxScore = 0;
    for (const field of searchFields) {
      const value = item[field.key];
      if (!value) continue;
      const result = fuzzyMatch(value, query, field.threshold || 50);
      const weightedScore = result.score * (field.weight || 1);
      if (result.match && weightedScore > maxScore) maxScore = weightedScore;
    }
    return { ...item, searchScore: maxScore };
  });
  
  return results.filter(item => item.searchScore > 0).sort((a, b) => b.searchScore - a.searchScore);
};

// Highlight matched text
const highlightMatch = (text, query) => {
  if (!text || !query) return text;
  const normQuery = normalizeText(query);
  const words = normQuery.split(/\s+/).filter(w => w.length > 0);
  if (words.length === 0) return text;
  
  let result = text;
  for (const word of words) {
    const regex = new RegExp(`(${word})`, 'gi');
    result = result.replace(regex, '|||$1|||');
  }
  
  return result.split('|||').map((part, index) => {
    const isMatch = words.some(w => normalizeText(part) === w);
    if (isMatch) return <span key={index} className="bg-yellow-500/40 text-yellow-200 px-0.5 rounded">{part}</span>;
    return part;
  });
};

// ========== END SEARCH SYSTEM ==========

// Parse date in DD/MM/YYYY or YYYY-MM-DD format
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  
  if (dateStr.includes('-') && dateStr.length === 10) {
    const [year, month, day] = dateStr.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }
  
  if (dateStr.includes('/')) {
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
  }
  
  return new Date(dateStr);
};

// Calculate days remaining based on entry_date + duration OR issue_date + duration OR expiry_date
const calculateDaysRemaining = (expiryDate, entryDate = null, durationDays = null, issueDate = null) => {
  // Priority: entry_date > issue_date > expiry_date
  const startDate = entryDate || issueDate;
  
  // If we have a start date and duration_days, calculate remaining days from them
  if (startDate && durationDays) {
    const start = parseDate(startDate);
    if (start && !isNaN(start.getTime())) {
      const calculatedExpiry = new Date(start);
      calculatedExpiry.setDate(calculatedExpiry.getDate() + parseInt(durationDays));
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      calculatedExpiry.setHours(0, 0, 0, 0);
      
      const diffTime = calculatedExpiry - today;
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
  }
  
  // Fallback to expiry_date
  if (!expiryDate) return null;
  
  const expiry = parseDate(expiryDate);
  if (!expiry || isNaN(expiry.getTime())) return null;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiry.setHours(0, 0, 0, 0);
  
  const diffTime = expiry - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Get status config based on days remaining - MATCHING MAIN SYSTEM
const getStatusConfig = (daysRemaining, isChangeStatus = false) => {
  if (isChangeStatus) {
    return {
      status: 'change_status',
      headerBg: 'bg-blue-500/15',
      headerBorder: 'border-blue-500/40',
      textColor: 'text-blue-400',
      icon: '🔄',
      text: 'STATUS CHANGED',
      cardBg: 'bg-gradient-to-br from-[#0F172A] to-[#1E293B]',
      cardBorder: 'border-2 border-blue-500/40',
      daysColor: '#3B82F6',
      showStamp: true
    };
  }
  
  if (daysRemaining === null) {
    return {
      status: 'active',
      headerBg: 'bg-emerald-500/15',
      headerBorder: 'border-emerald-500/40',
      textColor: 'text-emerald-400',
      icon: '✓',
      text: 'ACTIVE',
      cardBg: 'bg-gradient-to-br from-[#0F172A] to-[#1E293B]',
      cardBorder: 'border-none',
      daysColor: '#10B981',
      showStamp: false
    };
  }
  
  if (daysRemaining > 10) {
    return {
      status: 'active',
      headerBg: 'bg-emerald-500/15',
      headerBorder: 'border-emerald-500/40',
      textColor: 'text-emerald-400',
      icon: '✓',
      text: 'ACTIVE',
      cardBg: 'bg-gradient-to-br from-[#0F172A] to-[#1E293B]',
      cardBorder: 'border-none',
      daysColor: '#10B981',
      showStamp: false
    };
  }
  
  if (daysRemaining > 5) {
    return {
      status: 'expiring_soon',
      headerBg: 'bg-amber-500/25',
      headerBorder: 'border-amber-500/60',
      textColor: 'text-amber-400',
      icon: '⚠️',
      text: 'EXPIRING SOON',
      cardBg: 'bg-gradient-to-br from-[#1A1408] to-[#2D2310]',
      cardBorder: 'border-2 border-amber-500/40',
      daysColor: '#F59E0B',
      showStamp: false
    };
  }
  
  return {
    status: 'expired',
    headerBg: 'bg-gradient-to-r from-red-800 to-red-700',
    headerBorder: 'border-red-500',
    textColor: 'text-white',
    icon: '🚨',
    text: daysRemaining < 0 ? 'EXPIRED' : 'URGENT',
    cardBg: 'bg-gradient-to-br from-[#1F0A0A] to-[#2D1010]',
    cardBorder: 'border-2 border-red-500',
    daysColor: '#DC3545',
    showStamp: false
  };
};

// Public Tracker Page - Matching Main System Design
const PublicTrackerPage = () => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [showPin, setShowPin] = useState(false);
  
  // Data state
  const [activeTab, setActiveTab] = useState('visas');
  const [visas, setVisas] = useState([]);
  const [passports, setPassports] = useState({ pending: [], ready: [], sent: [], contractsNeedAction: [] });
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [passportSubTab, setPassportSubTab] = useState('pending'); // NEW: pending, ready, sent
  const [accountingStatus, setAccountingStatus] = useState(null); // NEW: Tadaber connection status
  
  // Stats
  const [visaStats, setVisaStats] = useState({
    total: 0,
    active: 0,
    soon: 0,
    urgent: 0
  });
  
  const [passportStats, setPassportStats] = useState({
    pending: 0,
    ready: 0,
    sent: 0,
    contractsNeedAction: 0
  });
  
  // Verify PIN
  const verifyPin = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/visa-tracker/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });
      
      const result = await response.json();
      
      if (result.valid) {
        setIsAuthenticated(true);
        setPinError('');
        loadAllData();
      } else {
        setPinError('رمز الدخول غير صحيح / Invalid PIN');
      }
    } catch (error) {
      setPinError('حدث خطأ في التحقق / Verification error');
    }
  };
  
  // Load all data
  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      // Load visas from the same endpoint as main system
      const visaResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/visas`);
      const visaData = await visaResponse.json();
      
      // Process visas - use entry_date OR issue_date + duration_days
      const processedVisas = (visaData || []).map(visa => {
        const daysRemaining = calculateDaysRemaining(
          visa.expiry_date, 
          visa.entry_date, 
          visa.duration_days || visa.duration,
          visa.issue_date
        );
        const statusConfig = getStatusConfig(daysRemaining, visa.linkedWorkerProcessStatus === 'CHANGE_STATUS');
        return { ...visa, daysRemaining, statusConfig };
      });
      
      setVisas(processedVisas);
      
      // Calculate visa stats
      const active = processedVisas.filter(v => v.statusConfig.status === 'active').length;
      const soon = processedVisas.filter(v => v.statusConfig.status === 'expiring_soon').length;
      const urgent = processedVisas.filter(v => v.statusConfig.status === 'expired').length;
      
      setVisaStats({
        total: processedVisas.length,
        active,
        soon,
        urgent
      });
      
      // Load passports
      const passportResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/passports/all`);
      const passportData = await passportResponse.json();
      
      // Separate pending passports into: no_contract (pending) and has_contract (ready)
      const allPending = passportData.pending || [];
      const pendingNoContract = allPending.filter(p => !p.has_contract);
      const readyWithContract = allPending.filter(p => p.has_contract);
      
      setPassports({
        pending: pendingNoContract,
        ready: readyWithContract,
        sent: passportData.sent || [],
        contractsNeedAction: passportData.contracts_need_action || []
      });
      
      setPassportStats({
        pending: pendingNoContract.length,
        ready: readyWithContract.length,
        sent: passportData.stats?.sent_count || passportData.sent?.length || 0,
        contractsNeedAction: passportData.stats?.contracts_need_action_count || 0
      });
      
      // Set accounting hub status
      setAccountingStatus(passportData.accounting_hub || null);
      
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Auto-refresh every 1 hour
  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(loadAllData, 60 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, loadAllData]);
  
  // Mark passport as sent - NEW FUNCTION
  const markPassportAsSent = async (visaId, method, notes = '') => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/visas/${visaId}/passport-sent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          passportSent: true,
          passportSentDate: new Date().toISOString().split('T')[0],
          passportSentBy: 'Employee Tracker',
          passportSentMethod: method,
          passportSentNotes: notes
        })
      });
      loadAllData(); // Refresh data
    } catch (error) {
      console.error('Error marking passport as sent:', error);
    }
  };
  
  // Search fields configuration
  const passportSearchFields = useMemo(() => [
    { key: 'passenger_name', weight: 1.5, threshold: 40 },
    { key: 'customer_name', weight: 1.2, threshold: 50 },
    { key: 'passport_number', weight: 1.0, threshold: 70 },
    { key: 'contract_number', weight: 0.8, threshold: 80 }
  ], []);
  
  const visaSearchFields = useMemo(() => [
    { key: 'name', weight: 1.5, threshold: 40 },
    { key: 'passport_number', weight: 1.0, threshold: 70 },
    { key: 'nationality', weight: 0.8, threshold: 60 }
  ], []);
  
  // Filter visas by advanced search
  const filteredVisas = useMemo(() => {
    if (!searchQuery || !searchQuery.trim()) return visas;
    return advancedSearch(visas, searchQuery, visaSearchFields);
  }, [visas, searchQuery, visaSearchFields]);
  
  // Filter passports by advanced search
  const filteredPendingPassports = useMemo(() => {
    if (!searchQuery || !searchQuery.trim()) return passports.pending;
    return advancedSearch(passports.pending, searchQuery, passportSearchFields);
  }, [passports.pending, searchQuery, passportSearchFields]);
  
  // Filter ready passports
  const filteredReadyPassports = useMemo(() => {
    if (!searchQuery || !searchQuery.trim()) return passports.ready;
    return advancedSearch(passports.ready, searchQuery, passportSearchFields);
  }, [passports.ready, searchQuery, passportSearchFields]);
  
  const filteredSentPassports = useMemo(() => {
    if (!searchQuery || !searchQuery.trim()) return passports.sent;
    return advancedSearch(passports.sent, searchQuery, passportSearchFields);
  }, [passports.sent, searchQuery, passportSearchFields]);
  
  // Visa Card Component - EXACT MATCH to main system
  const VisaCard = ({ visa }) => {
    const { daysRemaining, statusConfig } = visa;
    const [showDocs, setShowDocs] = useState(false);
    const isExpired = statusConfig.status === 'expired';
    const isExpiringSoon = statusConfig.status === 'expiring_soon';
    const isChangeStatus = statusConfig.status === 'change_status';
    
    const daysText = daysRemaining === null 
      ? 'No expiry date'
      : daysRemaining >= 0 
        ? `${daysRemaining} days remaining / يوم متبقي`
        : `Expired ${Math.abs(daysRemaining)} days ago`;
    
    // Format status change date
    const formatStampDate = (dateStr) => {
      if (!dateStr) return new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
    };
    
    return (
      <div 
        className={`${statusConfig.cardBg} ${statusConfig.cardBorder} rounded-2xl overflow-hidden transition-all ${isExpired ? 'animate-pulse shadow-[0_4px_30px_rgba(239,68,68,0.3)]' : isExpiringSoon ? 'shadow-[0_4px_25px_rgba(245,158,11,0.25)]' : 'shadow-lg'}`}
      >
        {/* Status Header */}
        <div className={`${statusConfig.headerBg} border-b-2 ${statusConfig.headerBorder} px-4 py-2.5 flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <span className={`text-lg ${isExpired ? 'text-xl' : ''}`}>{statusConfig.icon}</span>
            <span className={`${statusConfig.textColor} text-xs font-bold tracking-wide`}>
              {statusConfig.text}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {visa.linkedWorkerId && (
              <span className="text-emerald-400 text-xs">🔗 LINKED</span>
            )}
            {visa.documentCount > 0 && (
              <button
                type="button"
                onClick={() => setShowDocs(true)}
                className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-orange-500/15 border border-orange-500/40 text-orange-300 hover:bg-orange-500/30 transition-colors"
                title={`عرض ${visa.documentCount} مستند مرفق`}
                data-testid={`tracker-visa-doc-count-${visa.id}`}
              >
                <Paperclip className="h-3 w-3" />
                <span className="text-xs font-bold">{visa.documentCount}</span>
              </button>
            )}
            <span className="text-slate-500 text-xs font-mono">
              VISA #{(visa.visa_number || visa.id || '').toString().substring(0, 8).toUpperCase()}
            </span>
          </div>
        </div>

        {/* Attachments dialog (opened from the 📎 badge) */}
        <Dialog open={showDocs} onOpenChange={setShowDocs}>
          <DialogContent className="bg-[#1A2D47] border-[#004B93]/30 text-white max-w-md" data-testid="tracker-visa-docs-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-orange-400" />
                المستندات المرفقة / Attachments
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-white/60">{visa.name || visa.passenger_name}</p>
              <WorkerDocuments caseId={visa.linkedCaseId} readOnly compact />
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Alert Banner for Expiring/Expired */}
        {(isExpired || isExpiringSoon) && (
          <div className={`${isExpired ? 'bg-gradient-to-r from-red-600 via-red-700 to-red-600' : 'bg-gradient-to-r from-amber-600 via-amber-700 to-amber-600'} px-4 py-2 text-center`}>
            <p className="text-white text-xs font-bold tracking-wider">
              {isExpired 
                ? daysRemaining < 0 
                  ? '⚠️ إجراء عاجل مطلوب - URGENT ACTION REQUIRED ⚠️'
                  : `⚠️ VISA EXPIRING IN ${daysRemaining} DAYS - URGENT ⚠️`
                : 'الفـيـزا قـريـبة مـن الانـتـهاء - VISA EXPIRING SOON'
              }
            </p>
          </div>
        )}
        
        {/* Main Content */}
        <div className="p-4">
          {/* Worker Info */}
          <div className="flex items-start gap-3 mb-4">
            <div className="w-14 h-14 rounded-xl bg-[#1E3A5F] flex items-center justify-center flex-shrink-0 border border-[#3B82F6]/30">
              {visa.passenger_photo ? (
                <img src={formatBase64Image(visa.passenger_photo)} alt="" className="w-full h-full object-cover rounded-xl" />
              ) : (
                <User className="w-7 h-7 text-[#60A5FA]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-white text-lg leading-tight truncate">{visa.name || visa.passenger_name}</p>
              <p className="text-[#60A5FA] text-sm font-mono">{visa.passport_number}</p>
              <p className="text-white/50 text-xs">{visa.nationality}</p>
            </div>
          </div>
          
          {/* Visa Details - Grid matching main system */}
          <div className="grid grid-cols-3 gap-2 mb-3 bg-slate-900/50 rounded-xl p-3">
            <div className="text-center">
              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-0.5">COUNTRY / الدولة</p>
              <p className="text-white font-semibold text-sm">{visa.country || visa.visa_country || 'UAE'} 🇦🇪</p>
            </div>
            <div className="text-center border-l border-r border-slate-700">
              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-0.5">DURATION / المدة</p>
              <p className="text-[#F6C13B] font-bold text-sm">{visa.duration || visa.duration_days || '60'} Days</p>
            </div>
            <div className="text-center">
              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-0.5">EXPIRY / الانتهاء</p>
              <p className={`font-bold text-sm`} style={{ color: statusConfig.daysColor }}>{visa.expiry_date || '-'}</p>
            </div>
          </div>
          
          {/* VISA NUMBER Row - NEW */}
          <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-lg p-3 mb-3">
            <div className="text-center">
              <p className="text-cyan-400/70 text-[10px] uppercase tracking-wider mb-0.5">رقم الفيزا / VISA NUMBER</p>
              <p className="text-cyan-300 font-bold text-lg font-mono tracking-wider">{visa.visa_number || 'N/A'}</p>
            </div>
          </div>
          
          {/* Birth Date & Passport Expiry Row */}
          <div className="grid grid-cols-2 gap-2 mb-3 bg-slate-900/30 rounded-lg p-3">
            <div className="text-center">
              <p className="text-white/40 text-[9px] uppercase tracking-wider mb-0.5">🎂 تاريخ الميلاد / DOB</p>
              <p className="text-slate-300 text-sm font-medium">{visa.date_of_birth || 'N/A'}</p>
            </div>
            <div className="text-center border-l border-slate-700">
              <p className="text-white/40 text-[9px] uppercase tracking-wider mb-0.5">📕 انتهاء الجواز / Passport Exp</p>
              <p className="text-amber-400 text-sm font-medium">{visa.passport_expiry_date || visa.passport_expiry || 'N/A'}</p>
            </div>
          </div>
          
          {/* Days Remaining Box with STATUS CHANGED Stamp */}
          <div 
            className={`${statusConfig.headerBg} border ${statusConfig.headerBorder} rounded-xl p-4 text-center relative overflow-visible min-h-[80px]`}
            style={{ borderColor: statusConfig.daysColor }}
          >
            {/* STATUS CHANGED Stamp - Rectangular */}
            {statusConfig.showStamp && (
              <div className="mb-3 flex justify-center">
                <div className="border-4 border-emerald-500 rounded-lg px-6 py-2.5 bg-emerald-900/95 shadow-lg shadow-emerald-500/30">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-7 w-7 text-emerald-400" />
                    <div className="flex flex-col items-start">
                      <span className="text-emerald-300 text-sm font-black tracking-wider">STATUS CHANGED</span>
                      <span className="text-emerald-300 text-xs font-bold tracking-wider">تم تغيير الحالة</span>
                      <span className="text-emerald-200 text-[10px] font-medium mt-0.5">{formatStampDate(visa.status_change_date)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-base">⏳</span>
                <span className="text-slate-400 text-sm">{daysText}</span>
              </div>
              <div 
                className="px-3 py-1 rounded-lg"
                style={{ 
                  backgroundColor: `${statusConfig.daysColor}20`,
                  border: `1px solid ${statusConfig.daysColor}40`
                }}
              >
                <span className="text-xl font-bold" style={{ color: statusConfig.daysColor }}>
                  {daysRemaining !== null ? Math.abs(daysRemaining) : '-'}
                </span>
                <span className="text-xs font-medium ml-0.5" style={{ color: `${statusConfig.daysColor}80` }}>days</span>
              </div>
            </div>
          </div>
          
          {/* Linked Worker Section - NEW */}
          {(visa.linkedWorkerId || visa.linkedCaseId) && (
            <div className="mt-3 bg-gradient-to-r from-emerald-500/8 to-emerald-500/2 border border-emerald-500/25 rounded-xl p-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-sm">👤</span>
                  </div>
                  <div>
                    <p className="text-emerald-300 text-[10px] font-semibold uppercase tracking-wide">العامل المرتبط / Linked Worker</p>
                    <p className="text-white text-sm font-medium">{visa.linkedWorkerName || 'Unknown'}</p>
                  </div>
                </div>
                <span 
                  className={`px-2 py-1 rounded text-[10px] font-semibold ${
                    visa.linkedWorkerProcessStatus === 'CHANGE_STATUS' 
                      ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400' 
                      : 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-400'
                  }`}
                >
                  {visa.linkedWorkerProcessStatus === 'CHANGE_STATUS' 
                    ? '🔄 CHANGE STATUS' 
                    : (visa.linkedWorkerStatus || 'ABROAD').toUpperCase()}
                </span>
              </div>
            </div>
          )}
          
          {/* Supplier Info */}
          {visa.supplierName && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <p className="text-white/40 text-xs flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {visa.supplierName}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Passport Card Component - MATCHING /passport-tracker EXACTLY
  const PassportCard = ({ passport, status, onMarkSent }) => {
    const [showSendOptions, setShowSendOptions] = useState(false);
    
    const isPending = status === 'pending';
    const isReady = status === 'ready';
    const isSent = status === 'sent';
    const isRefund = isPending && passport.refund_alert;

    // Get status config
    const getStatusConfig = () => {
      if (isSent) {
        return {
          color: 'text-blue-400',
          bgColor: 'bg-blue-500/15',
          borderColor: 'border-blue-500',
          cardBg: 'bg-gradient-to-br from-blue-900/30 to-blue-950/50',
          text: 'SENT / تم الإرسال'
        };
      }
      if (isReady) {
        return {
          color: 'text-emerald-400',
          bgColor: 'bg-emerald-500/15',
          borderColor: 'border-emerald-500',
          cardBg: 'bg-gradient-to-br from-emerald-900/30 to-emerald-950/50',
          text: 'READY / جاهز'
        };
      }
      if (isRefund) {
        return {
          color: 'text-amber-300',
          bgColor: 'bg-amber-500/15',
          borderColor: 'border-amber-500/70',
          cardBg: 'bg-gradient-to-br from-amber-900/30 to-[#0F172A]',
          text: 'REFUNDED / تم الاسترجاع'
        };
      }
      return {
        color: 'text-red-400',
        bgColor: 'bg-red-500/15',
        borderColor: 'border-red-500',
        cardBg: 'bg-gradient-to-br from-red-900/30 to-red-950/50',
        text: 'PENDING / معلق'
      };
    };
    
    const statusConfig = getStatusConfig();
    const initials = (passport.passenger_name || 'UN').split(' ').map(n => n[0]).join('').substring(0,2).toUpperCase();
    
    return (
      <Card className={`relative rounded-2xl overflow-visible border-2 ${statusConfig.borderColor} ${statusConfig.cardBg}`}>
        {/* Status Header */}
        <div className={`${statusConfig.bgColor} border-b ${statusConfig.borderColor} px-4 py-3 flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            {isSent && <CheckCircle className={`h-4 w-4 ${statusConfig.color}`} />}
            {isReady && <CheckCircle className={`h-4 w-4 ${statusConfig.color}`} />}
            {isRefund && <RotateCcw className={`h-4 w-4 ${statusConfig.color}`} />}
            {isPending && !isRefund && <Clock className={`h-4 w-4 ${statusConfig.color}`} />}
            <span className={`${statusConfig.color} text-xs font-bold`}>
              {statusConfig.text}
            </span>
          </div>
          <span className="text-white/50 text-xs font-mono">
            #{(passport.passport_number || passport.id || '').substring(0, 12).toUpperCase()}
          </span>
        </div>

        {/* Alert Banners */}
        {isSent && (
          <div className="bg-blue-600 text-center py-2">
            <span className="text-white text-xs font-bold">✓ PASSPORT SENT - تم إرسال الجواز</span>
          </div>
        )}
        {isReady && (
          <div className="bg-emerald-600 text-center py-2">
            <span className="text-white text-xs font-bold">✓ READY TO SEND - جاهز للإرسال</span>
          </div>
        )}
        {isPending && (
          <div className="bg-red-600 text-center py-2">
            <span className="text-white text-xs font-bold">❌ NO CONTRACT - لم يتم التسديد</span>
          </div>
        )}

        {/* Main Content */}
        <CardContent className={`p-4 relative ${isSent ? 'opacity-80' : ''}`}>
          {/* PASSPORT SENT Stamp - Rectangular */}
          {isSent && (
            <div className="mb-3 flex justify-center">
              <div className="border-4 border-blue-500 rounded-lg px-6 py-2.5 bg-blue-900/95 shadow-lg shadow-blue-500/30">
                <div className="flex items-center gap-3">
                  <Send className="h-6 w-6 text-blue-400" />
                  <div className="flex flex-col items-start">
                    <span className="text-blue-300 text-sm font-black tracking-wider">PASSPORT SENT</span>
                    <span className="text-blue-300 text-xs font-bold tracking-wider">تم إرسال الجواز</span>
                    {passport.passportSentDate && (
                      <span className="text-blue-200 text-[10px] font-medium mt-0.5">
                        {new Date(passport.passportSentDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Person Info Row */}
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-12 h-12 rounded-xl ${statusConfig.bgColor} border ${statusConfig.borderColor} flex items-center justify-center overflow-hidden flex-shrink-0`}>
              {passport.passenger_photo ? (
                <img src={formatBase64Image(passport.passenger_photo)} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-[#F6C13B] font-bold text-sm">{initials}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-white truncate">{passport.passenger_name || 'Unknown'}</h3>
              <div className="flex items-center gap-2 text-xs text-white/50">
                {passport.passport_number && (
                  <span className="bg-white/10 px-2 py-0.5 rounded font-mono">{passport.passport_number}</span>
                )}
                {passport.nationality && <span>{passport.nationality}</span>}
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-[#0F172A]/50 rounded-lg p-3">
              <div className="text-[10px] text-white/50 mb-1 flex items-center gap-1">
                <CreditCard className="h-3 w-3" />
                رقم الجواز / Passport
              </div>
              <div className="text-sm font-medium text-white font-mono">{passport.passport_number || 'N/A'}</div>
            </div>
            <div className="bg-[#0F172A]/50 rounded-lg p-3">
              <div className="text-[10px] text-white/50 mb-1 flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                تاريخ الدخول / Entry
              </div>
              <div className="text-sm font-medium text-white">{passport.entry_date || 'N/A'}</div>
            </div>
          </div>

          {/* Sponsor Info */}
          {(passport.sponsor_name || passport.customer_name) && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-3">
              <div className="text-[10px] text-blue-400/70 mb-1 flex items-center gap-1">
                <User className="h-3 w-3" />
                Sponsor / الكفيل
              </div>
              <div className="text-sm text-blue-300 font-medium">{passport.sponsor_name || passport.customer_name}</div>
              {/* Phone Number */}
              {(passport.sponsor_phone || passport.customer_phone) && (
                <div className="mt-2 pt-2 border-t border-blue-500/20 flex items-center gap-2">
                  <span className="text-blue-400/60 text-xs">📞 Phone / الهاتف:</span>
                  <a 
                    href={`tel:${passport.sponsor_phone || passport.customer_phone}`}
                    className="text-blue-300 text-sm font-mono hover:text-blue-200"
                  >
                    {passport.sponsor_phone || passport.customer_phone}
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Contract Status Section */}
          {!isSent && (
            <div className={`rounded-xl p-3 mb-3 ${
              passport.has_contract 
                ? 'bg-emerald-500/10 border-2 border-emerald-500' 
                : passport.refund_alert
                  ? 'bg-amber-500/10 border-2 border-amber-500/70'
                  : 'bg-red-500/10 border-2 border-dashed border-red-500/50'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    passport.has_contract ? 'bg-emerald-500/20' : passport.refund_alert ? 'bg-amber-500/20' : 'bg-red-500/20'
                  }`}>
                    {passport.has_contract ? (
                      <CheckCircle className="h-5 w-5 text-emerald-400" />
                    ) : passport.refund_alert ? (
                      <RotateCcw className="h-5 w-5 text-amber-400" />
                    ) : (
                      <Clock className="h-5 w-5 text-red-400" />
                    )}
                  </div>
                  <div>
                    <p className={`font-bold text-sm ${
                      passport.has_contract ? 'text-emerald-400' : passport.refund_alert ? 'text-amber-400' : 'text-red-400'
                    }`}>
                      {passport.has_contract
                        ? 'تم التسديد - جاهز للإرسال'
                        : passport.refund_alert
                          ? 'تم استرجاع العقد'
                          : 'معلق - لم يتم التسديد'}
                    </p>
                    <p className={`text-xs ${
                      passport.has_contract ? 'text-emerald-400/60' : passport.refund_alert ? 'text-amber-400/70' : 'text-red-400/60'
                    }`}>
                      {passport.has_contract
                        ? 'Paid - Ready to Send'
                        : passport.refund_alert
                          ? 'Contract Refunded - Was Ready'
                          : 'Pending - No Contract'}
                    </p>
                  </div>
                </div>
              </div>
              {/* Refund details */}
              {!passport.has_contract && passport.refund_alert && (
                <div className="mt-3 pt-3 border-t border-amber-500/30 space-y-1.5 text-xs">
                  {passport.refunded_contract_number && (
                    <div className="flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5 text-amber-400/60" />
                      <span className="text-amber-300/70">عقد سابق / Previous contract:</span>
                      <span className="text-amber-200 font-mono font-bold">#{passport.refunded_contract_number}</span>
                    </div>
                  )}
                  {passport.refunded_customer_name && (
                    <div className="flex items-center gap-2">
                      <User className="h-3.5 w-3.5 text-amber-400/60" />
                      <span className="text-amber-300/70">عميل سابق / Previous client:</span>
                      <span className="text-amber-100 truncate">{passport.refunded_customer_name}</span>
                    </div>
                  )}
                  {passport.refunded_at && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5 text-amber-400/60" />
                      <span className="text-amber-300/70">تاريخ الاسترجاع / Refunded:</span>
                      <span className="text-amber-100">
                        {new Date(passport.refunded_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {/* Contract Number */}
              {passport.has_contract && passport.contract_number && (
                <div className="mt-3 pt-3 border-t border-emerald-500/20">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-emerald-400/50" />
                    <span className="text-emerald-400/70 text-xs">رقم العقد / Contract:</span>
                    <span className="text-emerald-300 text-sm font-mono font-bold">{passport.contract_number}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sent Info */}
          {isSent && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 mb-3">
              <div className="flex items-center gap-2 text-blue-400 text-sm font-bold mb-2">
                <CheckCircle className="h-4 w-4" />
                Sent Successfully / تم الإرسال بنجاح
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {passport.passportSentDate && (
                  <div>
                    <p className="text-white/40">Date / التاريخ</p>
                    <p className="text-white">{passport.passportSentDate}</p>
                  </div>
                )}
                {passport.passportSentMethod && (
                  <div>
                    <p className="text-white/40">Method / الطريقة</p>
                    <p className="text-white">{passport.passportSentMethod}</p>
                  </div>
                )}
              </div>
              {/* Contract Number Only - Sponsor info already shown above */}
              {passport.contract_number && (
                <div className="mt-3 pt-3 border-t border-blue-500/20">
                  <div className="flex items-center gap-2 text-xs">
                    <FileText className="h-3 w-3 text-blue-400/50" />
                    <span className="text-white/50">Contract / رقم العقد:</span>
                    <span className="text-blue-300 font-mono font-bold">{passport.contract_number}</span>
                  </div>
                </div>
              )}
              {passport.passportSentNotes && (
                <p className="text-white/40 text-xs mt-2">
                  Notes / ملاحظات: <span className="text-white">{passport.passportSentNotes}</span>
                </p>
              )}
            </div>
          )}

          {/* Send Actions (for ready tab only) */}
          {isReady && (
            <div className="mt-3">
              {!showSendOptions ? (
                <Button
                  onClick={() => setShowSendOptions(true)}
                  className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
                >
                  <Send className="h-4 w-4 mr-2" />
                  إرسال الجواز / Send Passport
                </Button>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-white/60 text-center mb-2">اختر طريقة الإرسال / Choose method:</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={() => {
                        onMarkSent(passport.id, 'Personal Pickup', '');
                        setShowSendOptions(false);
                      }}
                      variant="outline"
                      size="sm"
                      className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                    >
                      🤝 استلام شخصي
                    </Button>
                    <Button
                      onClick={() => {
                        onMarkSent(passport.id, 'Courier', '');
                        setShowSendOptions(false);
                      }}
                      variant="outline"
                      size="sm"
                      className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                    >
                      🚚 مندوب
                    </Button>
                    <Button
                      onClick={() => {
                        onMarkSent(passport.id, 'Shipping', '');
                        setShowSendOptions(false);
                      }}
                      variant="outline"
                      size="sm"
                      className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                    >
                      📦 شحن
                    </Button>
                    <Button
                      onClick={() => setShowSendOptions(false)}
                      variant="outline"
                      size="sm"
                      className="border-white/20 text-white/60"
                    >
                      ❌ إلغاء
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };
  
  // PIN Entry Screen - Matching main system style
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0F172A] via-[#1E293B] to-[#0F172A] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-[#1E293B]/80 backdrop-blur-xl border-[#3B82F6]/30 rounded-3xl overflow-hidden">
          <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2E4A6F] p-6 text-center border-b border-[#3B82F6]/30">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#D35400] to-[#A04000] flex items-center justify-center">
              <span className="text-4xl font-bold text-white">T</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">TADABER Tracker</h1>
            <p className="text-[#60A5FA] text-sm">متابعة الفيزا والجوازات</p>
            <p className="text-white/50 text-xs">Visa & Passport Tracking</p>
          </div>
          
          <CardContent className="p-6 space-y-4">
            <div className="text-center mb-4">
              <Lock className="h-8 w-8 mx-auto text-[#F6C13B] mb-2" />
              <p className="text-white/80 text-sm">أدخل رمز الدخول للمتابعة</p>
              <p className="text-white/50 text-xs">Enter PIN to continue</p>
            </div>
            
            <div className="relative">
              <Input
                type={showPin ? 'text' : 'password'}
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && verifyPin()}
                placeholder="● ● ● ●"
                className="text-center text-2xl tracking-[0.5em] bg-[#0F172A] border-[#3B82F6]/30 text-white placeholder:text-white/30 h-14 rounded-xl focus:border-[#3B82F6]"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/60 hover:text-white hover:bg-transparent"
                onClick={() => setShowPin(!showPin)}
              >
                {showPin ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </Button>
            </div>
            
            {pinError && (
              <p className="text-red-400 text-center text-sm">{pinError}</p>
            )}
            
            <Button
              onClick={verifyPin}
              disabled={!pin}
              className="w-full h-12 bg-gradient-to-r from-[#D35400] to-[#A04000] hover:from-[#E35D00] hover:to-[#B04500] text-white font-bold rounded-xl"
            >
              دخول / Enter
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Main Tracker Screen - Aviation Control Tower theme
  const fidsSample = [
    ...(visas || []).slice(0, 5).map(v => ({
      flag: '🌍', name: (v.passenger_name || '').slice(0, 22), passport: v.passport_number || '—',
      status: 'VISA', statusColor: 'text-cyan-300', extra: v.daysSinceEntry ? `${v.daysSinceEntry}d` : ''
    })),
    ...((passports?.pending || []).slice(0, 5).map(p => ({
      flag: p.nationality?.includes('PHIL') ? '🇵🇭' : p.nationality?.includes('INDO') ? '🇮🇩' : p.nationality?.includes('KEN') ? '🇰🇪' : '🌍',
      name: (p.passenger_name || '').slice(0, 22), passport: p.passport_number || '—',
      status: p.has_contract ? 'READY' : p.refund_alert ? 'REFUNDED' : 'PENDING',
      statusColor: p.has_contract ? 'text-emerald-400' : p.refund_alert ? 'text-amber-300' : 'text-red-400',
      extra: p.refund_alert ? `#${p.refunded_contract_number}` : p.has_contract ? `#${p.contract_number}` : `${p.daysSinceEntry || 0}d`,
    })))
  ];

  return (
    <OpsBackground>
      <div data-testid="public-tracker-page" />
      <OpsStatusBar
        accountingStatus={accountingStatus}
        stats={{}}
        refreshing={loading}
        onRefresh={() => loadAllData()}
        lastUpdate={lastUpdate}
        sample={fidsSample}
      />
      <div className="max-w-6xl mx-auto px-4 py-4 sm:py-6">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-black tracking-[0.2em] uppercase text-white" style={{ fontFamily: 'ui-monospace, "JetBrains Mono", "IBM Plex Mono", monospace' }}>
              <span className="text-amber-400">/</span> TADABER TRACKER
            </h1>
            <p className="text-[11px] font-mono tracking-wider text-white/40 uppercase">Visa &amp; Passport Tracking · متابعة الفيزا والجوازات</p>
          </div>
          {lastUpdate && (
            <div className="text-right text-[10px] font-mono text-white/40">
              <div className="text-white/60">LAST UPDATE</div>
              <div className="text-amber-300">{lastUpdate.toLocaleTimeString('en-GB', { hour12: false })}</div>
            </div>
          )}
        </div>

        {/* Tabs - Ops style */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveTab('visas')}
            data-testid="tab-visas"
            className={`flex-1 sm:flex-none px-5 h-11 rounded-xl border font-mono text-[12px] tracking-[0.18em] uppercase transition-all ${
              activeTab === 'visas'
                ? 'bg-cyan-500/15 border-cyan-500/50 text-cyan-300 ring-2 ring-cyan-500/20'
                : 'bg-white/5 border-white/10 text-white/50 hover:text-white hover:border-white/20'
            }`}
          >
            VISAS
          </button>
          <button
            onClick={() => setActiveTab('passports')}
            data-testid="tab-passports"
            className={`flex-1 sm:flex-none px-5 h-11 rounded-xl border font-mono text-[12px] tracking-[0.18em] uppercase transition-all ${
              activeTab === 'passports'
                ? 'bg-amber-500/15 border-amber-500/50 text-amber-300 ring-2 ring-amber-500/20'
                : 'bg-white/5 border-white/10 text-white/50 hover:text-white hover:border-white/20'
            }`}
          >
            PASSPORTS
          </button>
        </div>
        
        {/* Visas Tab */}
        {activeTab === 'visas' && (
          <>
            {/* Visa Stats - Ops KPI tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-4">
              <KpiTile label="TOTAL"   labelAr="إجمالي"   accent="ALL VISAS"  value={visaStats.total}  color="cyan"    icon={Sparkles}    sparkline={[3,4,3,5,6,5,7,8,9,10]} testId="visa-kpi-total" />
              <KpiTile label="ACTIVE"  labelAr="نشطة"     accent=">10 DAYS"   value={visaStats.active} color="emerald" icon={CheckCircle} sparkline={[1,2,2,3,3,4,5,6,7,8]}   testId="visa-kpi-active" />
              <KpiTile label="SOON"    labelAr="قريباً"   accent="6-10 DAYS"  value={visaStats.soon}   color="amber"   icon={Clock}       sparkline={[1,1,2,2,3,3,4,4,3,4]}   testId="visa-kpi-soon" />
              <KpiTile label="URGENT"  labelAr="عاجل"     accent="≤5 DAYS"    value={visaStats.urgent} color="red"     icon={AlertTriangle} sparkline={[0,1,1,2,2,3,3,4,4,5]} testId="visa-kpi-urgent" />
            </div>

            {/* Ops Search */}
            <div className="mb-4">
              <OpsSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                count={filteredVisas.length}
                bestScore={filteredVisas[0]?.searchScore || 0}
              />
            </div>
            
            {/* Visa Cards */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-80 rounded-2xl bg-[#1E293B]" />
                ))}
              </div>
            ) : filteredVisas.length === 0 ? (
              <div className="text-center py-20">
                <span className="text-6xl block mb-4">📋</span>
                <p className="text-white/60 text-lg">لا توجد فيزا</p>
                <p className="text-white/40 text-sm">No visas found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredVisas.map(visa => (
                  <VisaCard key={visa.id} visa={visa} />
                ))}
              </div>
            )}
          </>
        )}
        
        {/* Passports Tab - MATCHING /passport-tracker EXACTLY */}
        {activeTab === 'passports' && (
          <>
            {/* Passport KPI tiles (Ops style) */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-4">
              <KpiTile label="PENDING"       labelAr="لم يتم التسديد" accent="NO CONTRACT" value={passportStats.pending}              color="red"     icon={Clock}        sparkline={[2,3,4,3,5,6,7,8,9,10]} active={passportSubTab==='pending'}   onClick={() => setPassportSubTab('pending')}   testId="pp-kpi-pending" />
              <KpiTile label="READY TO SEND" labelAr="جاهز للإرسال"   accent="PAID"        value={passportStats.ready}                color="emerald" icon={CheckCircle}  sparkline={[1,2,3,4,5,7,8,9,11,12]} active={passportSubTab==='ready'}     onClick={() => setPassportSubTab('ready')}     testId="pp-kpi-ready" />
              <KpiTile label="REFUND ALERT"  labelAr="مرتجع"          accent="WAS READY"   value={passports.pending?.filter(p=>p.refund_alert).length || 0} color="amber" icon={RotateCcw} sparkline={[0,1,0,1,2,1,2,3]} onClick={() => setPassportSubTab('pending')}    testId="pp-kpi-refund" />
              <KpiTile label="SENT TODAY"    labelAr="تم الإرسال"     accent="DELIVERED"   value={passportStats.sent}                 color="blue"    icon={CheckCircle}  sparkline={[5,6,8,7,10,11,13,14,16,18]} active={passportSubTab==='sent'} onClick={() => setPassportSubTab('sent')}      testId="pp-kpi-sent" />
            </div>

            {/* Sub-tabs Ops-style */}
            <div className="flex flex-wrap gap-2 mb-3 text-[11px] font-mono">
              {[
                { id: 'pending',   label: 'PENDING',   count: passportStats.pending,             color: 'red' },
                { id: 'ready',     label: 'READY',     count: passportStats.ready,               color: 'emerald' },
                { id: 'sent',      label: 'SENT',      count: passportStats.sent,                color: 'blue' },
                { id: 'contracts', label: 'CONTRACTS', count: passportStats.contractsNeedAction, color: 'amber' },
              ].map((t) => {
                const active = passportSubTab === t.id;
                const cls = active
                  ? `bg-${t.color}-500/20 text-${t.color}-300 border-${t.color}-500/50`
                  : 'bg-white/5 text-white/50 border-white/10';
                return (
                  <button
                    key={t.id}
                    onClick={() => setPassportSubTab(t.id)}
                    className={`px-3 h-8 rounded-lg border tracking-widest uppercase transition ${cls}`}
                    data-testid={`pp-subtab-${t.id}`}
                  >
                    {t.label} <span className="opacity-60">· {t.count}</span>
                  </button>
                );
              })}
            </div>

            {/* Ops Search */}
            <div className="mb-4">
              <OpsSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                count={(passportSubTab === 'pending' ? filteredPendingPassports : passportSubTab === 'ready' ? filteredReadyPassports : passportSubTab === 'sent' ? filteredSentPassports : passports.contractsNeedAction || []).length}
                bestScore={0}
              />
            </div>
            
            {/* Contracts Need Action */}
            {passportSubTab === 'contracts' && (
              <div>
                {/* Header - Bilingual */}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 mb-4">
                  <div className="flex items-center gap-2 text-amber-400 text-sm">
                    <AlertTriangle className="h-4 w-4" />
                    <span>عقود تحتاج إجراء / Contracts Need Action</span>
                  </div>
                  <p className="text-amber-400/70 text-xs mt-1">
                    هذه العقود من نظام تدبر ولكن الجواز لم يتم استلامه بعد
                  </p>
                  <p className="text-amber-400/50 text-xs">
                    These contracts from Tadaber system but passport not yet collected
                  </p>
                </div>
                
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2].map(i => (
                      <Skeleton key={i} className="h-64 rounded-2xl bg-[#1E293B]" />
                    ))}
                  </div>
                ) : passports.contractsNeedAction?.length === 0 ? (
                  <div className="text-center py-10 bg-[#1E293B]/50 rounded-2xl border border-white/10">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 text-emerald-400" />
                    <p className="text-white/60">لا توجد عقود تحتاج إجراء</p>
                    <p className="text-white/40 text-sm">No contracts need action</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {passports.contractsNeedAction?.map(contract => (
                      <ContractCardEmployee 
                        key={contract.passport_number} 
                        contract={contract}
                        onPassportReceived={async (c) => {
                          try {
                            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contracts/passport-received/${c.passport_number}`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                worker_name: c.worker_name,
                                nationality: c.worker_nationality
                              })
                            });
                            loadAllData();
                          } catch (error) {
                            console.error('Error marking passport as received:', error);
                          }
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Pending Passports (No Contract) */}
            {passportSubTab === 'pending' && (
              <div>
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2].map(i => (
                      <Skeleton key={i} className="h-64 rounded-2xl bg-[#1E293B]" />
                    ))}
                  </div>
                ) : filteredPendingPassports.length === 0 ? (
                  <div className="text-center py-10 bg-[#1E293B]/50 rounded-2xl border border-white/10">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 text-emerald-400" />
                    <p className="text-white/60">لا توجد جوازات معلقة</p>
                    <p className="text-white/40 text-sm">No pending passports</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredPendingPassports.map(passport => (
                      <PassportCard 
                        key={passport.id} 
                        passport={passport} 
                        status="pending"
                        onMarkSent={markPassportAsSent}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Ready Passports (Has Contract) */}
            {passportSubTab === 'ready' && (
              <div>
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2].map(i => (
                      <Skeleton key={i} className="h-64 rounded-2xl bg-[#1E293B]" />
                    ))}
                  </div>
                ) : filteredReadyPassports.length === 0 ? (
                  <div className="text-center py-10 bg-[#1E293B]/50 rounded-2xl border border-white/10">
                    <FileText className="h-12 w-12 mx-auto mb-2 text-white/40" />
                    <p className="text-white/60">لا توجد جوازات جاهزة للإرسال</p>
                    <p className="text-white/40 text-sm">No passports ready to send</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredReadyPassports.map(passport => (
                      <PassportCard 
                        key={passport.id} 
                        passport={passport} 
                        status="ready"
                        onMarkSent={markPassportAsSent}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Sent Passports */}
            {passportSubTab === 'sent' && (
              <div>
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2].map(i => (
                      <Skeleton key={i} className="h-64 rounded-2xl bg-[#1E293B]" />
                    ))}
                  </div>
                ) : filteredSentPassports.length === 0 ? (
                  <div className="text-center py-10 bg-[#1E293B]/50 rounded-2xl border border-white/10">
                    <Send className="h-12 w-12 mx-auto mb-2 text-white/40" />
                    <p className="text-white/60">لا توجد جوازات مرسلة</p>
                    <p className="text-white/40 text-sm">No sent passports</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredSentPassports.map(passport => (
                      <PassportCard 
                        key={passport.id} 
                        passport={passport} 
                        status="sent"
                        onMarkSent={markPassportAsSent}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        
        {/* Refresh Button */}
        <div className="fixed bottom-6 right-6">
          <Button
            onClick={loadAllData}
            disabled={loading}
            className="w-14 h-14 rounded-full bg-gradient-to-r from-[#D35400] to-[#A04000] hover:from-[#E35D00] hover:to-[#B04500] shadow-lg"
          >
            <RefreshCw className={`h-6 w-6 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
    </OpsBackground>
  );
};

// Contract Card for Employees (without delete button) - Bilingual
const ContractCardEmployee = ({ contract, onPassportReceived }) => {
  return (
    <Card className="relative rounded-2xl overflow-visible border-2 border-amber-500/50 bg-gradient-to-br from-amber-900/20 to-[#0F172A]">
      {/* Status Header */}
      <div className="bg-amber-500/15 border-b border-amber-500/30 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-400" />
          <span className="text-amber-400 text-xs font-bold">
            عقد يحتاج إجراء / CONTRACT NEEDS ACTION
          </span>
        </div>
        <span className="text-white/50 text-xs font-mono">
          #{contract.contract_number}
        </span>
      </div>

      {/* Alert Banner - Bilingual */}
      <div className="bg-amber-600 text-center py-2">
        <span className="text-white text-xs font-bold">
          ⚠️ الجواز يجب استلامه من العميل السابق / Collect passport from previous client
        </span>
      </div>

      <CardContent className="p-4">
        {/* Worker Info */}
        <div className="flex items-start gap-4 mb-4">
          <div className="w-14 h-14 rounded-xl bg-amber-500/20 border-2 border-amber-500/40 flex items-center justify-center">
            <User className="h-7 w-7 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-bold text-base truncate">
              {contract.worker_name || 'غير معروف / Unknown'}
            </h3>
            <p className="text-amber-400/70 text-xs">
              {contract.worker_nationality || 'غير محدد / Not specified'}
            </p>
            <p className="text-white/40 text-xs mt-1">
              جواز / Passport: {contract.passport_number}
            </p>
          </div>
        </div>

        {/* Customer Info - Bilingual */}
        <div className="bg-amber-500/10 rounded-xl p-3 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <User className="h-4 w-4 text-amber-400" />
            <span className="text-amber-400 text-xs font-bold">العميل الجديد / New Customer</span>
          </div>
          <p className="text-white font-medium text-sm">
            {contract.customer_name}
          </p>
          {contract.customer_phone && (
            <div className="flex items-center gap-2 mt-2">
              <Phone className="h-3 w-3 text-amber-400/60" />
              <span className="text-amber-400/80 text-xs" dir="ltr">{contract.customer_phone}</span>
            </div>
          )}
        </div>

        {/* Payment Info - Bilingual */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="bg-[#1E293B]/50 rounded-lg p-2 text-center">
            <p className="text-white/40 text-[10px]">إجمالي العقد / Total</p>
            <p className="text-white font-bold text-xs">{contract.total_amount?.toLocaleString() || 0} AED</p>
          </div>
          <div className="bg-[#1E293B]/50 rounded-lg p-2 text-center">
            <p className="text-white/40 text-[10px]">المتبقي / Remaining</p>
            <p className={`font-bold text-xs ${contract.remaining_amount > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
              {contract.remaining_amount?.toLocaleString() || 0} AED
            </p>
          </div>
        </div>

        {/* Action Button - No Delete for employees - Bilingual */}
        <Button
          onClick={() => onPassportReceived(contract)}
          className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white font-bold text-xs"
        >
          <CheckCircle className="h-4 w-4 mr-2" />
          تم استلام الجواز / Passport Received
        </Button>
      </CardContent>
    </Card>
  );
};

export default PublicTrackerPage;
